
import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@env/environment';

import { tap, map, catchError, switchMap } from 'rxjs/operators';
import { EMPTY, forkJoin, Observable, of, throwError } from 'rxjs';
import { BillingExport } from '@app/shared/models/billing-export';
import * as moment from 'moment';
import swal from 'sweetalert2';
import { FilesService } from './files.service';
import { AccountAccessGuardService } from '../helpers/account-access-guard.service';
import { BlobDownloadService } from '../helpers/blob-download.service';


@Injectable({
    providedIn: 'root'
})
export class ReportsService {

    apiUrlV2 = environment.apiUrlV2;
    twelveMonthsDates: any[];

    constructor(
        private http: HttpClient,
        private filesService: FilesService,
        private accountAccessGuardService: AccountAccessGuardService,
        private blobDownloadService: BlobDownloadService
    ) {
        this.setTwelveMonthsDates();
    }
    private getCollectionData(options: any, responseType: 'json' | 'blob'): Observable<any> {
        options.relations = 'bill,billsPayment,billsPayment.patient,billsPayment.clinic,billsPayment.laboratories,progressNote';
        const url = `${this.apiUrlV2}/reports/collections`;
        const params = { ...options };
        const httpOptions: any = { params };

        if (responseType === 'blob') {
          httpOptions.responseType = 'blob';
        }

        return this.http.get(url, httpOptions).pipe(
          tap((res: any) => {
            if (responseType === 'json') {
              return res;
            } else {
                if(options.exportType == 'xlsx'){
                    this.blobDownloadService.downloadBlob(res, 'report.' + options.exportType);
                }
                else if(options.exportType == 'pdf'){
                    swal('Collection report generating', 'Collection report is being processed and will be emailed shortly.', 'info');
                }
            }
          }),
          catchError((error) => {
            if (error.status === 400 && options.exportType == 'pdf') {
                swal({
                    title: 'Error!',
                    text: 'The report contains too many records. Please reduce the data set and try again.',
                    type: 'warning',
                    showCancelButton: false,
                    confirmButtonColor: '#3085d6'
                  });
              }
            return throwError(error);
          })
        );
    }
    private getBillingData(options: any, responseType: 'json' | 'blob'): Observable<any> {
        options.relations = 'patient,clinic,laboratories,associate,billPayments.billsPaymentDetails';
        const url = `${this.apiUrlV2}/reports/billings`;
        const params = { ...options };
        const httpOptions: any = { params };

        if (responseType === 'blob') {
          httpOptions.responseType = 'blob';
        }

        return this.http.get(url, httpOptions).pipe(
          tap((res: any) => {
            if (responseType === 'json') {
              return res;
            } else {
              this.blobDownloadService.downloadBlob(res, 'report.' + options.exportType);
            }
          })
        );
    }
    private getExpenseData(options:any, responseType: 'json'| 'blob'): Observable<any>{
      options.relations = 'clinic';
      const url = `${this.apiUrlV2}/reports/expenses`;
      const params = { ...options };
      const httpOptions: any = { params };

      if (responseType === 'blob') {
        httpOptions.responseType = 'blob';
      }
      return this.http.get(url, httpOptions).pipe(
        tap((res: any) => {
          if (responseType === 'json') {
            return res;
          } else {
            this.blobDownloadService.downloadBlob(res, 'report.' + options.exportType);
          }
        })
      );
  }
    private getRecallData(options:any, responseType: 'json' | 'blob'): Observable<any>{
        options.relations = 'patient,associate,clinic';
        const url = `${this.apiUrlV2}/reports/recalls`;
        const params = { ...options };
        const httpOptions: any = { params };

        if (responseType === 'blob') {
          httpOptions.responseType = 'blob';
        }
        return this.http.get(url, httpOptions).pipe(
            tap((res: any) => {
              if (responseType === 'json') {
                return res;
              } else {
                this.blobDownloadService.downloadBlob(res, 'report.' + options.exportType);
              }
            })
        );
    }

    private getServiceIncomeData(options: any, responseType: 'json' | 'blob'): Observable<any> {
      const url = `${this.apiUrlV2}/reports/service-income`;
      const params = { ...options };
      if (Array.isArray(options.clinicIds)) {
        params['clinicIds[]'] = options.clinicIds;
        delete params.clinicIds;
      }
      if (Array.isArray(options.serviceIds)) {
        params['serviceIds[]'] = options.serviceIds;
        delete params.serviceIds;
      }
      const httpOptions: any = { params };

      if (responseType === 'blob') {
        httpOptions.responseType = 'blob';
      }

      return this.http.get(url, httpOptions).pipe(
        tap((res: any) => {
          if (responseType === 'blob') {
            this.blobDownloadService.downloadBlob(res, 'service-income-report.' + options.exportType);
          }
        })
      );
    }

    private getAccountCreditsData(options:any, responseType: 'json'| 'blob'): Observable<any>{
      const url = `${this.apiUrlV2}/reports/patient-account-credit-ledger`;
      const params = { ...options };
      const httpOptions: any = { params };
      return this.http.get(url, httpOptions).pipe(
        tap((res: any) => {
          if (responseType === 'json') {
            return res;
          }
        })
      );
  }
    getCollectionRecords(options: any): Observable<any> {
        return this.getCollectionData(options, 'json');
    }
    getCollectionToExport(options: any): Observable<any> {
        return this.getCollectionData(options, 'blob');
    }
    getBillingRecords(options:any): Observable<any> {
        return this.getBillingData(options, 'json');
    }
    getBillingToExport(options: any): Observable<BillingExport> {
        return this.http.post<BillingExport>(`${this.apiUrlV2}/reports/billings/exports`, { ...options });
    }
    getBillingExports(): Observable<BillingExport[]> {
        return this.http.get<BillingExport[]>(`${this.apiUrlV2}/reports/billings/exports`);
    }
    getBillingExportDownloadLink(id: string): Observable<{ url: string; expiresAt: string }> {
        return this.http.get<{ url: string; expiresAt: string }>(
            `${this.apiUrlV2}/reports/billings/exports/${encodeURIComponent(id)}/download`);
    }
    getRecallRecords(options:any): Observable<any>{
        return this.getRecallData(options, 'json');
    }
    getRecallToExport(options:any): Observable<any>{
        return this.getRecallData(options, 'blob');
    }
    getServiceIncomeRecords(options:any): Observable<any>{
        return this.getServiceIncomeData(options, 'json');
    }
    getServiceIncomeToExport(options:any): Observable<any>{
        return this.getServiceIncomeData(options, 'blob');
    }
    getExpenseRecords(options:any){
        return this.getExpenseData(options, 'json');
    }
    getAccountCreditsRecords(options:any){
      return this.getAccountCreditsData(options, 'json');
  }

    getAppliedAccountCreditsRecords(options:any){
        options.relations = 'bill.patient';
        const url = `${this.apiUrlV2}/reports/applied-account-credits`;
        const params = { ...options };
        const httpOptions: any = { params };
        return this.http.get(url, httpOptions).pipe(
            map((res: any) => res)
        );
    }
    private getOrthoPackageData(options: any, responseType: 'json' | 'blob'): Observable<any> {
        options.relations = 'patient,associate';
        const url = `${this.apiUrlV2}/reports/packages`;
        const params = { ...options };
        const httpOptions: any = { params };

        if (responseType === 'blob') {
            httpOptions.responseType = 'blob';
        }

        return this.http.get(url, httpOptions).pipe(
            tap((res: any) => {
                if (responseType === 'json') {
                    return res;
                } else {
                    const blobUrl = URL.createObjectURL(res);
                    const a = document.createElement('a');
                    a.href = blobUrl;
                    a.download = 'packages-report.' + options.exportType;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                }
            })
        );
    }
    getOrthoPackageRecords(options: any): Observable<any> {
        return this.getOrthoPackageData(options, 'json');
    }
    getOrthoPackageToExport(options: any): Observable<any> {
        return this.getOrthoPackageData(options, 'blob');
    }
    getExpenseToExport(options:any){
        return this.getExpenseData(options, 'blob');
    }
    getDeletedPatients(options:any){
        const url = `${this.apiUrlV2}/reports/deleted-patients`;
        return this.http.get(url, { params: options });
    }
    restorePatientRecords(options:any){

        return this.accountAccessGuardService.accountGuardRestriction(() => this.http.put(`${this.apiUrlV2}/reports/restore-deleted-patients`, { params: options }));
    }

    getPatientRecall(options: any){
        const url = `${this.apiUrlV2}/reports/patient-recalls`;
        options.relations = 'patient,clinic,activeAssociate';
        return this.http.get(url, { params: options });
    }

    getPatientAppointment(options: any){
        const url = `${this.apiUrlV2}/reports/patient-appointments`;
        options.relations = 'patient,clinic,activeAssociate';
        return this.http.get(url, { params: options });
    }

    getPatientLedgerRecords(options: any){
        const url = `${this.apiUrlV2}/reports/patient-ledger`;
        options.relations = 'patient,bill,billsPayment';
        return this.http.get(url, { params: options });
    }

    createBulkPayment(options:any){

        return this.accountAccessGuardService.accountGuardRestriction(() => this.http.post(`${this.apiUrlV2}/patient/bulk-payments/account-credits`, options).pipe(
            tap(
              (res: any) => {
                return of(res);
              },
              (error) => {
                swal({
                  title: 'Error!',
                  text: error.error.error,
                  type: 'warning',
                  showCancelButton: false,
                  confirmButtonColor: '#3085d6'
                });
                return of(null);
              }
            )
          ));
    }

    getLaboratoryCollections(options: any) {
        const url = this.apiUrlV2 + '/reports/collections/laboratory';
        return this.http.get(url, { params: options });
    }

    getAssociateCollections(options: any) {
        options.relations = 'bill,billsPayment,billsPayment.patient,billsPayment.clinic,billsPayment.laboratories,progressNote';
        const url = this.apiUrlV2 + '/reports/collections/associate';
        return this.http.get(url, { params: options });
    }

    setTwelveMonthsDates() {
        let dates: any = [];
        dates[0] = {
            month: moment().format('M'),
            label: moment().format('MMM YYYY')
        }
        for (let i = 0; i < 11; i++) {
            let prevMonth = moment().subtract(i + 1, 'months');
            dates[i + 1] = {
                month: prevMonth.format('M'),
                label: prevMonth.format('MMM YYYY'),
            }
        }
        this.twelveMonthsDates = dates.reverse();
    }
}
